import React, { useEffect, useState } from "react";
import { api } from "../lib/auth";

function badgeClass(status) {
  switch (status) {
    case "compliant":
      return "bg-green-900/40 text-green-300 border-green-700";
    case "warning":
      return "bg-yellow-900/40 text-yellow-300 border-yellow-700";
    case "critical":
      return "bg-red-900/40 text-red-300 border-red-700";
    case "expired":
      return "bg-red-950 text-red-200 border-red-600";
    case "missing":
      return "bg-zinc-800 text-zinc-300 border-zinc-600";
    default:
      return "bg-zinc-800 text-zinc-300 border-zinc-600";
  }
}

function statusLabel(item) {
  if (item?.status_label) return item.status_label;

  const days = item?.days_remaining;

  if (days === null || days === undefined) return "Expiry date missing";
  if (days < 0) return `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago`;
  if (days <= 30) return `Expires in ${days} day${days === 1 ? "" : "s"}`;
  if (days <= 90) return `Expires in ${days} days`;

  return `Valid for ${days} days`;
}

export default function PersonnelCompliancePage({
  userId,
  onSummaryChange,
}) {


  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [replacingId, setReplacingId] = useState(null);

  const [form, setForm] = useState({
    licence_type: "Security Licence",
    state_of_issue: "NSW",
    licence_number: "",
    issue_date: "",
    expiry_date: "",
    admin_notes: "",
  });

  const [document, setDocument] = useState(null);

  const load = async () => {
    try {
      const res = await api.get(`/personnel-compliance/user/${userId}`);

      const payload = res.data || {};
      const nextItems = Array.isArray(payload) ? payload : payload.items || [];

      setItems(nextItems);
      setSummary(Array.isArray(payload) ? null : payload.summary || null);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!userId) return;

      try {
        await load();
} catch (err) {
        console.error("Failed to initialise Personnel Compliance page", err);
      }
    };

    init();
  }, [userId]);

  useEffect(() => {
    if (!summary) return;

    onSummaryChange?.({
      score: Number(summary.score || 0),
      expired: Number(summary.expired || 0),
      critical: Number(summary.critical || 0),
      warning: Number(summary.warning || 0),
      missing: Number(summary.missing || 0),
      deployable: Boolean(summary.deployable),
    });
  }, [summary, onSummaryChange]);

  const submit = async (e) => {
    e.preventDefault();

    try {
      setUploading(true);

      const data = new FormData();

      Object.entries(form).forEach(([k, v]) => {
        data.append(k, v);
      });

      if (document) {
        data.append("document", document);
      }

      await api.post(`/personnel-compliance/user/${userId}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setForm({
        licence_type: "Security Licence",
        state_of_issue: "NSW",
        licence_number: "",
        issue_date: "",
        expiry_date: "",
        admin_notes: "",
      });

      setDocument(null);

      load();
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const replaceDocument = async (item, file) => {
    if (!file) return;

    try {
      setReplacingId(item.id);

      const data = new FormData();
      data.append("licence_type", item.licence_type || "");
      data.append("state_of_issue", item.state_of_issue || "NSW");
      data.append("licence_number", item.licence_number || "");
      data.append("issue_date", item.issue_date || "");
      data.append("expiry_date", item.expiry_date || "");
      data.append("admin_notes", item.admin_notes || "");
      data.append("document", file);

      await api.put(`/personnel-compliance/licence/${item.id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      await load();
    } catch (err) {
      console.error(err);
      alert("Document replacement failed");
    } finally {
      setReplacingId(null);
    }
  };

  const filteredItems =
    statusFilter === "all"
      ? items
      : items.filter((item) => item.compliance_status === statusFilter);

  const filterOptions = [
    { value: "all", label: "All" },
    { value: "compliant", label: "Valid" },
    { value: "warning", label: "Warning" },
    { value: "critical", label: "Critical" },
    { value: "expired", label: "Expired" },
    { value: "missing", label: "Missing" },
  ];

return (
    <div className="space-y-6 pb-24">

      
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2">
            <div className="text-xs text-zinc-400">Compliance Score</div>
            <div className="text-2xl font-bold text-white">{summary.score || 0}%</div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2">
            <div className="text-xs text-zinc-400">Expired</div>
            <div className="text-2xl font-bold text-red-400">{summary.expired || 0}</div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2">
            <div className="text-xs text-zinc-400">Critical</div>
            <div className="text-2xl font-bold text-red-300">{summary.critical || 0}</div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2">
            <div className="text-xs text-zinc-400">Warning</div>
            <div className="text-2xl font-bold text-yellow-300">{summary.warning || 0}</div>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2">
            <div className="text-xs text-zinc-400">Deployable</div>
            <div className={`text-xl font-bold ${summary.deployable ? "text-green-300" : "text-red-300"}`}>
              {summary.deployable ? "Yes" : "No"}
            </div>
          </div>
        </div>
      )}

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2">
        <h2 className="text-base font-semibold mb-2">Licences & Qualifications</h2>

        <form onSubmit={submit} className="grid grid-cols-2 gap-2">
          <select
            className="bg-zinc-800 border border-zinc-700 rounded-lg p-2"
            value={form.licence_type}
            onChange={(e) => setForm({ ...form, licence_type: e.target.value })}
          >
            <option>Security Licence</option>
            <option>First Aid Certificate</option>
            <option>Driver Licence</option>
            <option>White Card</option>
            <option>RSA</option>
            <option>Police Check</option>
          </select>

          <input
            className="bg-zinc-800 border border-zinc-700 rounded-lg p-2"
            placeholder="Licence Number"
            value={form.licence_number}
            onChange={(e) => setForm({ ...form, licence_number: e.target.value })}
          />

          <input
            type="date"
            className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 col-span-2"
            value={form.expiry_date}
            onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
          />

          <input
            type="file"
            className="bg-zinc-800 border border-zinc-700 rounded-lg p-2 col-span-2"
            onChange={(e) => setDocument(e.target.files[0])}
          />

          <button
            type="submit"
            disabled={uploading}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-lg p-2"
          >
            {uploading ? "Uploading..." : "Add Licence"}
          </button>
        </form>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-2">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h3 className="text-base font-semibold">Existing Licences</h3>

          <div className="flex flex-wrap gap-2">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setStatusFilter(option.value)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                  statusFilter === option.value
                    ? "border-blue-500 bg-blue-500/20 text-blue-300"
                    : "border-zinc-700 bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2 pb-4">
          {filteredItems.map((item) => (
            <div
              key={item.id || item._id}
              className="border border-zinc-700 rounded-lg px-3 py-2 bg-zinc-950/30"
            >
              <div className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr_1fr_auto] gap-2 items-center">
                <div>
                  <div className="font-semibold">{item.licence_type}</div>
                  <div className="text-xs text-zinc-500">Type</div>
                </div>

                <div>
                  <div className="text-sm text-zinc-300 break-all">
                    {item.licence_number || "No licence number recorded"}
                  </div>
                  <div className="text-xs text-zinc-500">Licence Number</div>
                </div>

                <div>
                  <div className="text-sm text-zinc-300">
                    {item.expiry_date || "Not provided"}
                  </div>
                  <div className="text-xs text-zinc-500">Expiry Date</div>
                </div>

                <span
                  className={`justify-self-start md:justify-self-end inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${badgeClass(
                    item.compliance_status
                  )}`}
                >
                  {statusLabel(item)}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-zinc-800">
                {item.document_url ? (
                  <a
                    href={item.document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-400 text-sm hover:underline"
                  >
                    View Document
                  </a>
                ) : (
                  <span className="text-xs text-orange-400">No document uploaded</span>
                )}

                <label className="text-sm text-zinc-300 border border-zinc-700 rounded-md px-3 py-1 cursor-pointer hover:bg-zinc-800">
                  {replacingId === item.id || replacingId === item._id
                    ? "Uploading..."
                    : item.document_url
                      ? "Replace Document"
                      : "Upload Document"}
                  <input
                    type="file"
                    className="hidden"
                    accept="application/pdf,image/*"
                    disabled={replacingId === item.id || replacingId === item._id}
                    onChange={(e) => replaceDocument(item, e.target.files?.[0])}
                  />
                </label>
              </div>
            </div>
          ))}

          {items.length === 0 && (
            <div className="text-zinc-500 text-sm">No licences uploaded yet.</div>
          )}

          {items.length > 0 && filteredItems.length === 0 && (
            <div className="text-zinc-500 text-sm">
              No licences match this filter.
            </div>
          )}

          {items.length > 0 && filteredItems.length === 0 && (
            <div className="text-zinc-500 text-sm">
              No licences match this filter.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
